import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import getUserId from '../../../functions/getUserId';
import api from '../../../functions/api';
import { CLAUSE_COOLDOWN_MODE_OPTIONS } from '../utils';

// Helper to stabilize object for comparison (sort keys)
const stabilizeConfig = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stabilizeConfig);
  return Object.keys(obj).sort().reduce((acc, key) => {
    const val = obj[key];
    if (val !== undefined) { // Keep nulls but skip undefined
      acc[key] = stabilizeConfig(val);
    }
    return acc;
  }, {});
};

export const useBacktestConfig = (callAction, startPortfolios = []) => {
  const [configOpen, setConfigOpen] = useState(false);
  const [savedConfigurations, setSavedConfigurations] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [loadedConfig, setLoadedConfig] = useState(null);
  const [configValidationErrors, setConfigValidationErrors] = useState([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  const [config, setConfig] = useState({
    start_portfolio_id: null,
    start_date: '2023-01-01',
    end_date: new Date().toISOString().split('T')[0],
    scenario: null,
    alert_ids: [],
    alert_selection: 'combined_accumulate',
    execution_price_model: 'open_next_day',
    position_sizing: {
      buy: {
        if_new_position: { mode: 'fixed_amount', amount: 2000 },
        if_existing_position: { mode: 'percent_equity', percent: 2.0 },
      },
      sell: {
        mode: 'percent_position', percent: 100
      }
    },
    cash_policy: {
      mode: 'keep_percent',
      target_pct: 15
    },
    start_cash_eur: null,  // null = use portfolio cash; only set explicitly to override
    allow_negative_cash: false,
    restrict_alerts_to_overlap: true,
    risk: {
      max_leverage_multiple: 1.5,
      max_borrow_multiple: 0.5,
      borrow_rate_annual_pct: 5.0,
      maintenance_margin_pct: 0.25
    },
    transaction_costs: {
      mode: 'fixed',
      buy_fixed_eur: 2.0,
      sell_fixed_eur: 2.0,
      buy_bps: 0,
      sell_bps: 0
    },
    // DAS-1773: Slippage modeling (bid-ask spread + market impact)
    slippage: {
      enabled: false,
      large_cap_pct: 0.02,  // >€10B market cap (2 bps - realistic)
      mid_cap_pct: 0.08,    // €2-10B market cap (8 bps - realistic)
      small_cap_pct: 0.35,  // <€2B market cap (35 bps - realistic)
      large_cap_threshold: 10000000000,  // €10B
      mid_cap_threshold: 2000000000,     // €2B
    },
    dividends: {
      schedule: 'quarterly',
      include_special: false
    },
    splits: {
      price_ratio_threshold: 1.5
    },
    liquidity_filters: {
      enabled: false,
      min_market_cap_eur: null,
      min_avg_daily_dollar_volume: null
    },
    // DAS-1741: Currency/exchange restriction filters
    currency_filters: {
      enabled: false,
      allowed_currencies: null, // null = all, or array like ["USD", "EUR"]
      excluded_exchanges: [],   // array of suffixes like [".T", ".HK", ".TW"]
      stock_region: 'all'       // DAS-1778: 'all', 'us_only', or 'non_us_only'
    },
    benchmark: {
      secondary: null, // Optional secondary benchmark (e.g., 'MSCI World (EUR)')
      strategy: 'excess_returns_over_index',
      alternatives: ['STOXX Europe 600 (EUR)', 'S&P 500 (EUR)', 'Your existing portfolio performance']
    },
    survivorship_bias: {
      avoid_survivorship_bias: false
    },
    engine: {
      sample_limit: 200,
      cooldown_days_default: 0 // Default to 0 to rely on clause-specific cooldowns
    },
    data_gaps: {
      mode: 'skip'
    },
    constraints: {
      max_positions: null,
      max_exposure_per_ticker_pct: null
    },
    backtest_clause_ids: [],
    backtest_clause_set_id: null,
    backtest_clause_mode: 'clauses', // 'clauses' or 'clause_set'
    clause_cooldowns: {}, // Map clause_id -> { mode: 'days'|'count', value: number }
    clause_cooldown_default: { mode: 'days', value: 28 },
    clause_position_sizing: {}, // Map clause_id -> { buy_amount: number|null, sell_percent: number|null }
    // DAS-1643: Rebalancing configuration
    rebalancing: {
      enabled: false,
      frequency_days: 63,
      selection_percentile: 98,
      sector_neutral: true,
      // DAS-1688: Max sector exposure (only used when sector_neutral=false)
      max_sector_exposure_pct: 40,  // 10-100, 100 = no limit
      // DAS-1689: Z-score baseline
      z_score_baseline: 'universe',  // 'universe' or 'market'
      // DAS-1764: Excluded sectors
      // DAS-1775: Added 'Unknown' as default excluded (tickers with NULL sector)
      excluded_sectors: ['Unknown'],
      // DAS-1775: Exclude tickers with unknown market cap
      exclude_unknown_market_cap: true,
      // DAS-1762: Momentum filter
      momentum_filter: {
        enabled: false,
        min_return_pct: 0,  // Minimum momentum (0 = positive only)
        lookback_months: 12,
        skip_recent_month: true  // Classic 12-1 momentum
      },
      // DAS-1686: Factor strategy configuration
      factor_strategy: {
        preset: 'value',  // 'value' | 'growth' | 'custom'
        roa_weight: 50,   // 0-100, only used when preset='custom'
        pb_weight: 50     // 0-100, only used when preset='custom'
      }
    },
    // DAS-1763: Regime-based factor weight adjustment (applies to rebalancing)
    regime_adjustment: {
      enabled: false,
      index: 'sp500',         // 'sp500' or 'msci_world'
      vix_bear_threshold: 25, // VIX above this = BEAR signal
      vix_bull_threshold: 20, // VIX below this = BULL signal
      hysteresis_days: 5,     // Minimum days before regime switch
      bear_roa_mult: 1.15,    // ROA weight multiplier in BEAR regime
      bear_pb_mult: 0.85,     // P/B weight multiplier in BEAR regime  
      neutral_roa_mult: 0.9,  // ROA weight multiplier in NEUTRAL (defensive)
      neutral_pb_mult: 2.0,   // P/B weight multiplier in NEUTRAL (value tilt)
      bull_roa_mult: 0.85,    // ROA weight multiplier in BULL regime
      bull_pb_mult: 1.15,     // P/B weight multiplier in BULL regime
      bear_position_mult: 0.7,    // Position size multiplier in BEAR (-30%)
      neutral_position_mult: 0.85, // Position size multiplier in NEUTRAL (-15%)
      bull_position_mult: 1.2,    // Position size multiplier in BULL (+20%)
      bear_cash_pct: 30,          // Cash reserve % in BEAR (defensive)
      neutral_cash_pct: 20,       // Cash reserve % in NEUTRAL (cautious)
      bull_cash_pct: 0,           // Cash reserve % in BULL (fully invested)
    },
  });

  // Derived state for signal source
  const signalSource = useMemo(() => {
    if (config.backtest_clause_set_id) return 'clause_set';
    if (config.backtest_clause_ids && config.backtest_clause_ids.length > 0) return 'clauses';
    if (config.backtest_clause_mode === 'clause_set') return 'clause_set';
    if (config.backtest_clause_mode === 'clauses') return 'clauses';
    return 'alerts';
  }, [config.backtest_clause_set_id, config.backtest_clause_ids, config.backtest_clause_mode]);

  const loadSavedConfigurations = async () => {
    const data = await callAction('list_user_configurations', {});
    if (data?.success) {
      setSavedConfigurations(data.data?.configurations || []);
    }
  };

  const loadConfiguration = async (configurationId, startPortfolios, setSelected) => {
    const data = await callAction('get_run_config', { configuration_id: configurationId });
    if (data?.success && data.data) {
      const loaded = data.data.configuration || data.data;
      
      // Convert dates from "Sun, 01 Jan 2023 00:00:00 GMT" to "2023-01-01"
      const convertDate = (dateStr) => {
        if (!dateStr) return '';
        try {
          const d = new Date(dateStr);
          return d.toISOString().split('T')[0]; // Returns YYYY-MM-DD
        } catch {
          return dateStr; // Return as-is if conversion fails
        }
      };
      
      // DAS-1691: Map backend _json suffix fields to frontend field names
      // Backend stores: position_sizing_json, cash_policy_json, transaction_costs_json, etc.
      // Frontend uses: position_sizing, cash_policy, transaction_costs, etc.
      const loadedPositionSizing = loaded.position_sizing_json || loaded.position_sizing;
      const loadedCashPolicy = loaded.cash_policy_json || loaded.cash_policy;
      const loadedTransactionCosts = loaded.transaction_costs_json || loaded.transaction_costs;
      const loadedDividends = loaded.dividends_json || loaded.dividends;
      const loadedSplits = loaded.splits_json || loaded.splits;
      
      // Extract _ui_extras from constraints_json (backend stores clause config here)
      let uiExtras = {};
      try {
        const constraintsJson = typeof loaded.constraints_json === 'string' 
          ? JSON.parse(loaded.constraints_json) 
          : loaded.constraints_json;
        uiExtras = constraintsJson?._ui_extras || {};
      } catch {
        uiExtras = {};
      }
      
      // Merge with defaults to ensure new fields exist
      // DAS-1691: Use mapped fields (without _json suffix) for proper merging
      const merged = {
        ...config,
        ...loaded,
        ...uiExtras, // Extract clause config from _ui_extras
        start_date: convertDate(loaded.start_date),
        end_date: convertDate(loaded.end_date),
        // DAS-1691: Properly merge nested objects using mapped fields
        position_sizing: loadedPositionSizing ? {
          buy: {
            if_new_position: { ...config.position_sizing.buy.if_new_position, ...(loadedPositionSizing.buy?.if_new_position || {}) },
            if_existing_position: { ...config.position_sizing.buy.if_existing_position, ...(loadedPositionSizing.buy?.if_existing_position || {}) },
          },
          sell: { ...config.position_sizing.sell, ...(loadedPositionSizing.sell || {}) }
        } : config.position_sizing,
        cash_policy: loadedCashPolicy ? { ...config.cash_policy, ...loadedCashPolicy } : config.cash_policy,
        transaction_costs: loadedTransactionCosts ? { ...config.transaction_costs, ...loadedTransactionCosts } : config.transaction_costs,
        slippage: { ...config.slippage, ...(uiExtras.slippage || loaded.slippage || {}) },  // DAS-1773
        dividends: loadedDividends ? { ...config.dividends, ...loadedDividends } : config.dividends,
        splits: loadedSplits ? { ...config.splits, ...loadedSplits } : config.splits,
        risk: { ...config.risk, ...(uiExtras.risk || loaded.risk || {}) },
        liquidity_filters: { ...config.liquidity_filters, ...(uiExtras.liquidity_filters || loaded.liquidity_filters || {}) },
        currency_filters: { ...config.currency_filters, ...(uiExtras.currency_filters || loaded.currency_filters || {}) },  // DAS-1741
        benchmark: { ...config.benchmark, ...(uiExtras.benchmark || loaded.benchmark || {}) },
        survivorship_bias: { ...config.survivorship_bias, ...(uiExtras.survivorship_bias || loaded.survivorship_bias || {}) },
        engine: { ...config.engine, ...(uiExtras.engine || loaded.engine || {}) },
        data_gaps: { ...config.data_gaps, ...(loaded.data_gaps || {}) },
        constraints: { ...config.constraints, ...(loaded.constraints || {}) },
        rebalancing: { ...config.rebalancing, ...(uiExtras.rebalancing || loaded.rebalancing || {}) },  // DAS-1660: Check _ui_extras first
        regime_adjustment: { ...config.regime_adjustment, ...(uiExtras.regime_adjustment || loaded.regime_adjustment || {}) },  // DAS-1763
      };
      
      setConfig(merged);
      setLoadedConfig(JSON.stringify(stabilizeConfig(merged)));
      
      // If portfolio ID is in config, try to select it
      if (loaded.start_portfolio_id && startPortfolios && setSelected) {
        const pf = startPortfolios.find(p => p.id === loaded.start_portfolio_id);
        if (pf) {
          setSelected(pf);
        } else {
          // DAS-1731: Portfolio not in local list - clear selection but config still has the ID
          // UI will show "#{id} (not in local list)" message
          setSelected(null);
          console.warn(`Start portfolio #${loaded.start_portfolio_id} not found in local list (${startPortfolios.length} portfolios loaded)`);
        }
      }
      
      setEditingConfigId(configurationId);
      message.success('Configuration loaded');
    } else {
      message.error('Failed to load configuration');
    }
  };

  const saveConfiguration = async (configurationId) => {
    // Generate descriptive name: <scenario>,<portfolio>,<benchmark>,<interval>,<datetime>
    const generateName = () => {
      const parts = [];
      
      // 1. Scenario
      if (config.scenario) {
        parts.push(config.scenario);
      } else if (config.backtest_clause_set_id) {
        parts.push(`ClauseSet#${config.backtest_clause_set_id}`);
      } else if (config.backtest_clause_ids && config.backtest_clause_ids.length > 0) {
        parts.push(`Clauses(${config.backtest_clause_ids.length})`);
      } else if (config.alert_ids && config.alert_ids.length > 0) {
        parts.push(`Alerts(${config.alert_ids.length})`);
      } else if (config.rebalancing?.enabled) {
        parts.push('Rebalancing');
      } else {
        parts.push('Custom');
      }
      
      // 2. Portfolio name (from selected portfolio)
      const portfolioName = startPortfolios?.find(p => p.id === config.start_portfolio_id)?.name || 'NoPortfolio';
      parts.push(portfolioName);
      
      // 3. Benchmark
      const benchmark = config.benchmark?.secondary || 'Buy-and-Hold';
      parts.push(benchmark);
      
      // 4. Time interval
      const interval = `${config.start_date || '?'}_${config.end_date || '?'}`;
      parts.push(interval);
      
      // 5. Last edit datetime
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
      parts.push(timestamp);
      
      return parts.join(', ');
    };
    
    // Always generate a new descriptive name (even for updates)
    const name = generateName();
    
    const payload = {
      ...config, // Spread all config fields at top level
      name: name
    };
    
    // If updating existing config, add configuration_id
    if (configurationId) {
      payload.configuration_id = configurationId;
    }
    // Backend will handle INSERT if configuration_id is missing
    
    const res = await callAction('update_configuration', payload);
    console.log('[saveConfiguration] Response:', res);
    console.log('[saveConfiguration] res.data:', res.data);
    if (res?.success) {
      message.success('Configuration saved');
      loadSavedConfigurations();
      // Set as current editing config
      const newId = res.data?.configuration_id || res.data?.id || configurationId;
      console.log('[saveConfiguration] res.data.configuration_id:', res.data?.configuration_id);
      console.log('[saveConfiguration] res.data.id:', res.data?.id);
      console.log('[saveConfiguration] configurationId param:', configurationId);
      console.log('[saveConfiguration] newId:', newId);
      if (newId) {
        setEditingConfigId(newId);
        // Update config state to include the name
        const savedConfig = { ...config, name: name };
        setConfig(savedConfig);
        // Update loadedConfig with the saved payload
        const stabilizedStr = JSON.stringify(stabilizeConfig(savedConfig));
        console.log('[saveConfiguration] Setting loadedConfig');
        setLoadedConfig(stabilizedStr);
        console.log('[saveConfiguration] Done - editingConfigId should now be:', newId);
      } else {
        console.error('[saveConfiguration] No newId returned!');
      }
    } else {
      console.error('[saveConfiguration] Save failed:', res);
      message.error('Failed to save configuration');
    }
  };

  const validateConfiguration = (cfg) => {
    const errors = [];
    if (!cfg.start_portfolio_id) errors.push('Start portfolio is required');
    if (!cfg.start_date) errors.push('Start date is required');
    if (!cfg.end_date) errors.push('End date is required');
    
    // If rebalancing is enabled, we don't need alerts/clauses
    if (!config.rebalancing.enabled) {
      const hasAlerts = cfg.alert_ids && Array.isArray(cfg.alert_ids) && cfg.alert_ids.length > 0;
      const hasClauses = cfg.backtest_clause_ids && Array.isArray(cfg.backtest_clause_ids) && cfg.backtest_clause_ids.length > 0;
      const hasClauseSet = cfg.backtest_clause_set_id != null;
      if (!hasAlerts && !hasClauses && !hasClauseSet) {
        errors.push('Select at least one alert, clause, or clause combination');
      }
    }
    return errors;
  };

  const handleStartDateChange = (e) => {
    setConfig({ ...config, start_date: e.target.value });
  };

  const handleEndDateChange = (e) => {
    setConfig({ ...config, end_date: e.target.value });
  };

  // Helpers for date manipulation
  const formatDate = (d) => d.toISOString().split('T')[0];
  const addDays = (start, days) => {
    const d = new Date(start);
    d.setDate(d.getDate() + days);
    return d;
  };
  const addMonthsClamped = (start, monthsToAdd) => {
    const d = new Date(start);
    const day = d.getDate();
    d.setMonth(d.getMonth() + monthsToAdd);
    if (d.getDate() !== day) {
      d.setDate(0);
    }
    return d;
  };

  const applyEndPreset = (preset) => {
    if (!config.start_date) {
      message.warning('Set start date first');
      return;
    }
    const start = new Date(config.start_date);
    let end = null;
    switch (preset) {
      case '1w': end = addDays(start, 7); break;
      case '1m': end = addMonthsClamped(start, 1); break;
      case '3m': end = addMonthsClamped(start, 3); break;
      case '6m': end = addMonthsClamped(start, 6); break;
      case '9m': end = addMonthsClamped(start, 9); break;
      case '1y': end = addMonthsClamped(start, 12); break;
      case '1.5y': end = addMonthsClamped(start, 18); break;
      case '2y': end = addMonthsClamped(start, 24); break;
      default: return;
    }
    if (end) {
      const today = new Date();
      if (end > today) end = today; // Clamp to today
      setConfig(c => ({ ...c, end_date: formatDate(end) }));
    }
  };

  const handleRebalancingToggle = (enabled) => {
    setConfig(c => {
      const next = { ...c, rebalancing: { ...c.rebalancing, enabled } };
      // If enabling rebalancing, clear signal sources to avoid confusion
      if (enabled) {
        next.alert_ids = [];
        next.backtest_clause_ids = [];
        next.backtest_clause_set_id = null;
        next.scenario = null;
      }
      return next;
    });
  };

  const handleSignalSourceChange = (mode) => {
    setConfig((c) => {
      const next = { ...c, backtest_clause_mode: mode === 'clause_set' ? 'clause_set' : 'clauses' };
      if (mode === 'alerts') {
        next.backtest_clause_ids = [];
        next.backtest_clause_set_id = null;
      } else if (mode === 'clauses') {
        next.alert_ids = [];
        next.scenario = null;
        next.backtest_clause_set_id = null;
      } else if (mode === 'clause_set') {
        next.alert_ids = [];
        next.scenario = null;
        next.backtest_clause_ids = [];
      }
      return next;
    });
  };

  const updateClauseCooldownDefault = (patch) => {
    setConfig(c => ({ ...c, clause_cooldown_default: { ...c.clause_cooldown_default, ...patch } }));
  };

  const upsertClauseOverride = (key, patch) => {
    setConfig(c => {
      const next = { ...c.clause_cooldowns };
      const existing = next[key] || { mode: 'days', value: 0 };
      next[key] = { ...existing, ...patch };
      return { ...c, clause_cooldowns: next };
    });
  };

  const clearClauseOverride = (key) => {
    setConfig(c => {
      const next = { ...c.clause_cooldowns };
      delete next[key];
      return { ...c, clause_cooldowns: next };
    });
  };

  const upsertClausePositionSizing = (key, patch) => {
    setConfig(c => {
      const next = { ...c.clause_position_sizing };
      const existing = next[key] || { buy_amount: null, sell_percent: null };
      next[key] = { ...existing, ...patch };
      return { ...c, clause_position_sizing: next };
    });
  };

  const clearClausePositionSizing = (key) => {
    setConfig(c => {
      const next = { ...c.clause_position_sizing };
      delete next[key];
      return { ...c, clause_position_sizing: next };
    });
  };

  const isDirty = useMemo(() => {
    // For new/fictive configs (no editingConfigId), always enable Save button
    if (!editingConfigId) return true;
    // For existing configs, compare against loaded snapshot
    if (!loadedConfig) return false;
    const currentStr = JSON.stringify(stabilizeConfig(config));
    return currentStr !== loadedConfig;
  }, [config, editingConfigId, loadedConfig]);

  return {
    config,
    setConfig,
    configOpen,
    setConfigOpen,
    savedConfigurations,
    selectedConfigId,
    setSelectedConfigId,
    editingConfigId,
    setEditingConfigId,
    configValidationErrors,
    setConfigValidationErrors,
    alertModalOpen,
    setAlertModalOpen,
    signalSource,
    loadSavedConfigurations,
    loadConfiguration,
    saveConfiguration,
    validateConfiguration,
    handleStartDateChange,
    handleEndDateChange,
    applyEndPreset,
    handleRebalancingToggle,
    handleSignalSourceChange,
    updateClauseCooldownDefault,
    upsertClauseOverride,
    clearClauseOverride,
    upsertClausePositionSizing,
    clearClausePositionSizing,
    isDirty
  };
};

